<template>
  <div
    class="ap-si"
    :class="{ 'ap-field': !compact, 'ap-si--compact': compact }"
    ref="root"
  >
    <label v-if="label">{{ label }} <span v-if="required" class="ap-req">*</span></label>

    <div
      class="ap-si-trigger"
      :class="{ 'is-open': open, 'has-icon': !!icon, 'is-disabled': disabled }"
      @click="toggle"
    >
      <fa-icon v-if="icon" :icon="icon" class="ap-lead" />
      <span class="ap-si-val" :class="{ 'is-placeholder': !selectedLabel }">
        {{ selectedLabel || placeholder }}
      </span>
      <fa-icon
        v-if="clearable && value"
        :icon="['fas', 'times']"
        class="ap-si-clear"
        @click.stop="$emit('input', '')"
      />
      <fa-icon :icon="['fas', 'chevron-down']" class="ap-si-chevron" :class="{ 'is-open': open }" />
    </div>

    <!--
      Menu is rendered here by Vue (so reactivity / refs work normally),
      but immediately moved to <body> in mounted() so that position:fixed
      coordinates are always relative to the true viewport — immune to any
      ancestor CSS transform, will-change, filter, or overflow that would
      otherwise create a new fixed containing block.
    -->
    <div
      v-show="open"
      ref="menu"
      class="ap-si-menu"
      :style="menuStyle"
      @click.stop
    >
      <div
        v-for="opt in options"
        :key="opt.value"
        class="ap-si-opt"
        :class="{ 'is-active': value === opt.value }"
        @click="select(opt)"
      >
        <span class="ap-si-opt-label">{{ opt.label }}</span>
        <fa-icon v-if="value === opt.value" :icon="['fas', 'check']" class="ap-si-opt-check" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SelectInput',
  props: {
    value: { type: [String, Number], default: '' },
    label: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Select…' },
    required: { type: Boolean, default: false },
    icon: { type: Array, default: null },
    compact: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false }
  },
  data () {
    return { open: false, menuStyle: {} }
  },
  computed: {
    selectedLabel () {
      const opt = this.options.find(o => o.value === this.value)
      return opt ? opt.label : ''
    }
  },
  mounted () {
    document.addEventListener('click', this.onOutsideClick)
    window.addEventListener('resize', this.close)
    window.addEventListener('scroll', this.onScroll, true)

    // Portal: move menu out of the component tree into <body>.
    // This means position:fixed on .ap-si-menu is always relative to the
    // actual viewport, regardless of transforms on any ancestor element.
    document.body.appendChild(this.$refs.menu)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onOutsideClick)
    window.removeEventListener('resize', this.close)
    window.removeEventListener('scroll', this.onScroll, true)

    // Remove portal node from body on teardown
    const menu = this.$refs.menu
    if (menu && menu.parentNode === document.body) {
      document.body.removeChild(menu)
    }
  },
  methods: {
    toggle () {
      if (this.disabled) return
      this.open = !this.open
      if (this.open) this.$nextTick(this.positionMenu)
    },
    positionMenu () {
      const trigger = this.$el && this.$el.querySelector('.ap-si-trigger')
      if (!trigger) return
      const r = trigger.getBoundingClientRect()
      this.menuStyle = {
        position: 'fixed',
        top: (r.bottom + 4) + 'px',
        left: r.left + 'px',
        width: r.width + 'px',
        zIndex: 9999
      }
    },
    close () {
      this.open = false
    },
    onScroll () {
      if (this.open) this.positionMenu()
    },
    select (opt) {
      this.$emit('input', opt.value)
      this.open = false
    },
    onOutsideClick (e) {
      // Check both the trigger root AND the portalled menu (now a body child)
      const root = this.$refs.root
      const menu = this.$refs.menu
      const insideRoot = root && root.contains(e.target)
      const insideMenu = menu && menu.contains(e.target)
      if (!insideRoot && !insideMenu) {
        this.open = false
      }
    }
  }
}
</script>
